import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import TableContainer from "../../../components/Common/DataTableContainer";

const Maquinas = () => {
    document.title = "Máquinas | GraphicForce - Printesco";

    const [maquinas, setMaquinas] = useState([]);
    const [maquinasInactivas, setMaquinasInactivas] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [form, setForm] = useState({ nombre: "" });
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [maquinaToDelete, setMaquinaToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [maquinaToRestore, setMaquinaToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Fetch todas las máquinas y separarlas por estado
    const fetchMaquinas = async () => {
        try {
            // Obtener todas las máquinas de una vez
            const resultList = await pb.collection("maquina").getList(1, 1000, {
                sort: "-created"
            });
            
            // Separar máquinas activas e inactivas
            const activas = resultList.items.filter(maquina => maquina.activo !== false);
            const inactivas = resultList.items.filter(maquina => maquina.activo === false);
            
            // Aplicar paginación solo a las activas
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activasPaginadas = activas.slice(startIndex, endIndex);
            
            setMaquinas(activasPaginadas);
            setMaquinasInactivas(inactivas);
            setTotalRecords(activas.length);
        } catch (err) {
            console.error("Error fetching máquinas:", err);
        }
    };

    useEffect(() => {
        fetchMaquinas();
    }, [pageIndex, pageSize]);

    const reloadMaquinas = () => {
        fetchMaquinas();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "" });
        setModalError("");
        setModalAdd(true);
    };

    const handleRemove = (maquina) => {
        setMaquinaToDelete(maquina);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!maquinaToDelete) return;
        setDeleting(true);
        try {
            await pb.collection("maquina").update(maquinaToDelete.id, { activo: false });
            fetchMaquinas();
            setShowDeleteAlert(false);
            setMaquinaToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar máquina");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setMaquinaToDelete(null);
    };

    // Show alert and store maquina to restore
    const handleRestore = (maquina) => {
        setMaquinaToRestore(maquina);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!maquinaToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("maquina").update(maquinaToRestore.id, { activo: true });
            fetchMaquinas();
            setShowRestoreAlert(false);
            setMaquinaToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar máquina");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setMaquinaToRestore(null);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === "nombre") {
            setForm({ ...form, [name]: value.toUpperCase() });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                activo: true,
            };
            await pb.collection("maquina").create(data);
            fetchMaquinas();
            setModalAdd(false);
        } catch (err) {
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `La máquina con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar máquina");
            }
        }
        setSaving(false);
    };

    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const maquina = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="danger" size="sm" onClick={() => handleRemove(maquina)} disabled={maquina.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [maquinas]);

    // Columnas para máquinas inactivas
    const columnsInactivas = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const maquina = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(maquina)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [maquinasInactivas]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Centro de Costos" breadcrumbItem="Máquinas" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && maquinaToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará la máquina <b>{maquinaToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && maquinaToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará la máquina <b>{maquinaToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Máquinas
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadMaquinas}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={maquinas}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Máquinas Inactivas */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Máquinas Inactivas
                                        <div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                title="Recargar tabla"
                                                onClick={reloadMaquinas}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columnsInactivas}
                                                data={maquinasInactivas}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                isCustomPageSize={false}
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Máquina</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                placeholder="Ingrese el nombre de la máquina"
                                required
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default Maquinas;
